import React from 'react'
import { ChevronLeft, ChevronRight, ScrollText, RefreshCw } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import AdminSidebar from '@/features/admin/components/AdminSidebar'
import { useAdminLogs } from '@/features/admin/hooks/useAdminLogs'
import { actionLabel, actionColor } from '@/features/admin/utils/auditLabels'
import type { AuditLog } from '@/features/admin/types/index.js'

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const SkeletonRow: React.FC<{ border: string; secondary: string }> = ({ border, secondary }) => (
  <tr style={{ borderTop: `1px solid ${border}` }}>
    <td className="px-5 py-4">
      <div className="h-6 rounded-full animate-pulse w-36" style={{ backgroundColor: border }} />
    </td>
    <td className="px-5 py-4">
      <div className="h-3.5 rounded-full animate-pulse w-28" style={{ backgroundColor: secondary }} />
    </td>
    <td className="px-5 py-4">
      <div className="h-3.5 rounded-full animate-pulse w-28" style={{ backgroundColor: secondary }} />
    </td>
    <td className="px-5 py-4">
      <div className="h-3.5 rounded-full animate-pulse w-36" style={{ backgroundColor: secondary }} />
    </td>
  </tr>
)

function MetadataCell({
  metadata,
  border,
  secondary,
}: {
  metadata: Record<string, unknown> | null | undefined
  border: string
  secondary: string
}) {
  const entries = Object.entries(metadata ?? {})
  if (entries.length === 0) return <span className="text-xs">—</span>
  return (
    <div className="flex flex-col gap-1 max-w-md">
      {entries.map(([k, v]) => {
        if (v !== null && typeof v === 'object') {
          return (
            <details key={k} className="text-xs font-mono">
              <summary className="cursor-pointer select-none">{k}</summary>
              <pre
                className="mt-1 whitespace-pre-wrap break-words rounded-lg p-2 text-xs"
                style={{ backgroundColor: secondary, border: `1px solid ${border}` }}
              >
                {JSON.stringify(v, null, 2)}
              </pre>
            </details>
          )
        }
        return (
          <span key={k} className="text-xs font-mono">
            {k}: {String(v)}
          </span>
        )
      })}
    </div>
  )
}

const AdminLogsPage: React.FC = () => {
  const { currentTheme } = useTheme()
  const { data, loading, error, page, setPage, refresh } = useAdminLogs(1, 25)

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / 25))

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.primary }}>
      <AdminSidebar />

      <main className="lg:pl-56 min-h-screen">
        <div className="px-4 pt-16 pb-8 sm:px-6 lg:px-8 lg:pt-8">
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: currentTheme.text }}>
                Activity Log
              </h1>
              <p className="mt-1 text-sm" style={{ color: currentTheme.secondrytext }}>
                All admin actions recorded on the platform
              </p>
            </div>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition disabled:opacity-40"
              style={{
                backgroundColor: currentTheme.secondary,
                color: currentTheme.text,
                borderColor: currentTheme.border,
              }}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {error && (
            <div
              className="mb-6 rounded-2xl px-5 py-4 text-sm"
              style={{
                backgroundColor: currentTheme.errorBackground,
                color: currentTheme.errorText,
                border: `1px solid ${currentTheme.border}`,
              }}
            >
              {error}
            </div>
          )}

          <div
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: currentTheme.border }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <colgroup>
                  <col style={{ width: '180px' }} />
                  <col style={{ width: '160px' }} />
                  <col style={{ width: '160px' }} />
                  <col className="w-auto" />
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: currentTheme.secondary }}>
                    {['Action', 'Performed By', 'Target', 'Details'].map((h, i) => (
                      <th
                        key={i}
                        className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: currentTheme.secondrytext }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <SkeletonRow key={i} border={currentTheme.border} secondary={currentTheme.secondary} />
                    ))
                  ) : (data?.data ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <ScrollText size={28} style={{ color: currentTheme.mutedText }} />
                          <p className="text-sm font-medium" style={{ color: currentTheme.secondrytext }}>
                            No activity recorded yet
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    (data?.data ?? []).map((log: AuditLog) => {
                      const color = actionColor(log.action, currentTheme)
                      return (
                        <tr
                          key={log.id}
                          style={{ borderTop: `1px solid ${currentTheme.border}` }}
                        >
                          <td className="px-5 py-3.5">
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                              style={{ backgroundColor: color.bg, color: color.text }}
                            >
                              {actionLabel(log.action)}
                            </span>
                            <p className="text-xs mt-1" style={{ color: currentTheme.mutedText }}>
                              {formatDate(log.createdAt)}
                            </p>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-xs font-medium truncate max-w-[140px]" style={{ color: currentTheme.text }}>
                              {log.performedByName}
                            </p>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-xs" style={{ color: currentTheme.secondrytext }}>
                              {log.targetType}
                            </p>
                            <p className="text-xs font-medium truncate max-w-[140px] mt-0.5" style={{ color: currentTheme.text }}>
                              {log.targetName}
                            </p>
                          </td>
                          <td className="px-5 py-3.5" style={{ color: currentTheme.secondrytext }}>
                            <MetadataCell
                              metadata={log.metadata}
                              border={currentTheme.border}
                              secondary={currentTheme.secondary}
                            />
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-5 flex items-center justify-between">
            <p className="text-sm" style={{ color: currentTheme.secondrytext }}>
              {(data?.total ?? 0) > 0
                ? `Showing ${(page - 1) * 25 + 1}–${Math.min(page * 25, data?.total ?? 0)} of ${data?.total ?? 0}`
                : '0 logs'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage(page - 1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: currentTheme.secondary,
                  color: currentTheme.text,
                  borderColor: currentTheme.border,
                }}
              >
                <ChevronLeft size={14} />
                Previous
              </button>
              <span className="text-xs px-1" style={{ color: currentTheme.mutedText }}>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage(page + 1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: currentTheme.secondary,
                  color: currentTheme.text,
                  borderColor: currentTheme.border,
                }}
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default AdminLogsPage
